/**
 * Group Quiz Host Page
 * Shows QR code and real-time participant list, then transitions to full-screen game display
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import './groupQuizHost.css';

interface GroupQuiz {
    id: number;
    hash: string;
    quizId: number;
    quizTitle: string;
    hostUsername: string;
    dateTaken: string;
    dateCompleted: string | null;
    userNames: string[];
    whoWon: string | null;
}

interface Question {
    id: number;
    quizId: number;
    questionType: number;
    question: string;
    answer: string;
    answer_choices_json: string;
    answerExplanation?: string | null;
}

interface LeaderboardEntry {
    username: string;
    score: number;
}

type GameState = 'waiting' | 'active' | 'reveal' | 'completed';

export default function GroupQuizHost() {
    const { hash } = useParams<{ hash: string }>();
    const navigate = useNavigate();
    const [groupQuiz, setGroupQuiz] = useState<GroupQuiz | null>(null);
    const [participants, setParticipants] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [ws, setWs] = useState<WebSocket | null>(null);
    const [copied, setCopied] = useState(false);

    // Game state
    const [gameState, setGameState] = useState<GameState>('waiting');
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [questionNumber, setQuestionNumber] = useState(0);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
    const [answeredUsernames, setAnsweredUsernames] = useState<string[]>([]);
    const [correctAnswer, setCorrectAnswer] = useState('');
    const [countdown, setCountdown] = useState(0);
    const [connectionStatuses, setConnectionStatuses] = useState<Record<string, boolean>>({});

    // Fetch group quiz details
    useEffect(() => {
        const fetchGroupQuiz = async () => {
            try {
                const response = await fetch(`/api/group-quiz/${hash}`);
                if (!response.ok) {
                    throw new Error('Group quiz not found');
                }

                const data = await response.json();
                setGroupQuiz(data.groupQuiz);
                setParticipants(data.groupQuiz.userNames || []);
                setLoading(false);
            } catch (err: any) {
                setError(err.message);
                setLoading(false);
            }
        };

        fetchGroupQuiz();
    }, [hash]);

    // Setup WebSocket connection for real-time updates
    useEffect(() => {
        if (!hash) return;

        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${protocol}//${window.location.host}/ws`;
        const websocket = new WebSocket(wsUrl);

        websocket.onopen = () => {
            console.log('[HOST] WebSocket connected');
            // Join the group quiz room
            websocket.send(JSON.stringify({
                type: 'join-room',
                roomId: hash,
                username: 'host',
                isHost: true,
            }));
        };

        websocket.onmessage = (event) => {
            const data = JSON.parse(event.data);
            console.log('[HOST] Received message:', data);

            if (data.type === 'participant-joined') {
                setParticipants(data.participants);
            }

            if (data.type === 'participants-update') {
                setParticipants(data.participants);
            }

            if (data.type === 'connection-status') {
                setConnectionStatuses(data.connectionStatuses);
            }

            if (data.type === 'quiz-started') {
                setTotalQuestions(data.totalQuestions);
            }

            if (data.type === 'show-question') {
                setCurrentQuestion(data.question);
                setQuestionNumber(data.questionNumber);
                setGameState('active');
                setAnsweredUsernames([]);
                setCorrectAnswer('');
            }

            if (data.type === 'player-answered') {
                setAnsweredUsernames(data.answeredUsernames);
            }

            if (data.type === 'all-answered') {
                setGameState('reveal');
                setCorrectAnswer(data.correctAnswer);
                setLeaderboard(data.leaderboard);
            }

            if (data.type === 'next-question') {
                setCountdown(data.countdown);
            }

            if (data.type === 'quiz-completed') {
                setGameState('completed');
                setLeaderboard(data.leaderboard);
            }

            if (data.type === 'quiz-cancelled') {
                navigate('/');
            }
        };

        websocket.onerror = (error) => {
            console.error('[HOST] WebSocket error:', error);
        };

        websocket.onclose = () => {
            console.log('[HOST] WebSocket disconnected');
        };

        setWs(websocket);

        return () => {
            websocket.close();
        };
    }, [hash, navigate]);

    const joinUrl = `${window.location.origin}/group-quiz/${hash}/join`;

    const handleStart = async () => {
        if (!hash) return;

        try {
            const response = await fetch(`/api/group-quiz/${hash}/start`, {
                method: 'POST',
            });

            if (!response.ok) {
                console.error('Failed to start quiz');
                const data = await response.json();
                setError(data.error || 'Failed to start quiz');
            }
        } catch (error) {
            console.error('Error starting quiz:', error);
            setError('Failed to start quiz');
        }
    };

    const handleCancel = async () => {
        if (!hash) return;

        try {
            const response = await fetch(`/api/group-quiz/${hash}/cancel`, {
                method: 'POST',
            });

            if (response.ok) {
                navigate('/');
            } else {
                console.error('Failed to cancel quiz');
                // Still navigate away on error
                navigate('/');
            }
        } catch (error) {
            console.error('Error cancelling quiz:', error);
            // Still navigate away on error
            navigate('/');
        }
    };

    const handleDone = () => {
        navigate('/');
    };

    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(joinUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy URL:', err);
        }
    };

    if (loading) {
        return <div className="groupQuizHostContainer">Loading...</div>;
    }

    if (error || !groupQuiz) {
        return (
            <div className="groupQuizHostContainer">
                <div className="error">
                    <h2>Error</h2>
                    <p>{error || 'Group quiz not found'}</p>
                    <button onClick={() => navigate('/')}>Go Home</button>
                </div>
            </div>
        );
    }

    // Game completed screen
    if (gameState === 'completed') {
        const winner = leaderboard[0];

        return (
            <div className="hostGameContainer">
                <div className="completedScreen">
                    <h1>Congratulations!</h1>
                    {winner && (
                        <div className="winnerAnnouncement">
                            <p className="winnerLabel">Winner</p>
                            <p className="winnerName">{winner.username}</p>
                            <p className="winnerScore">{winner.score} points</p>
                        </div>
                    )}
                    <div className="finalLeaderboardVertical">
                        <h2>Final Rankings</h2>
                        {leaderboard.map((entry, index) => (
                            <div key={entry.username} className="leaderboardEntryVertical">
                                <span className="rank">#{index + 1}</span>
                                <span className="name">{entry.username}</span>
                                <span className="score">{entry.score}</span>
                            </div>
                        ))}
                    </div>
                    <button className="doneButton" onClick={handleDone}>
                        Done
                    </button>
                </div>
            </div>
        );
    }

    // Game active/reveal screen
    if (gameState === 'active' || gameState === 'reveal') {
        return (
            <div className="hostGameContainer">
                <div className="gameHeader">
                    <div className="questionProgress">
                        Question {questionNumber} of {totalQuestions}
                    </div>
                </div>

                <div className="gameContent">
                    {currentQuestion && (
                        <>
                            {gameState === 'active' ? (
                                <h1 className="questionTextLarge">{currentQuestion.question}</h1>
                            ) : (
                                <div className="revealSection">
                                    <h2 className="correctAnswerLabel">Correct Answer:</h2>
                                    <h1 className="correctAnswerLarge">{correctAnswer}</h1>
                                    {countdown > 0 && (
                                        <div className="countdownSlider">
                                            <div className="countdownText">Next question in {countdown}s...</div>
                                            <div className="countdownBar">
                                                <div
                                                    className="countdownProgress"
                                                    style={{ width: `${(countdown / 5) * 100}%` }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="leaderboardHorizontal">
                    {leaderboard.map((entry, index) => (
                        <div key={entry.username} className="leaderboardPlayer">
                            <div className="playerRank">#{index + 1}</div>
                            <div className="playerName">
                                {entry.username}
                                {answeredUsernames.includes(entry.username) && gameState === 'active' && (
                                    <span className="checkmark">✓</span>
                                )}
                                {connectionStatuses[entry.username] === false && (
                                    <span className="disconnectedIndicator" title="Disconnected">⚠️</span>
                                )}
                            </div>
                            <div className="playerScore">{entry.score}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Waiting room screen (default)
    return (
        <div className="groupQuizHostContainer">
            <div className="header">
                <h1>{groupQuiz.quizTitle}</h1>
                <p className="hostInfo">Hosted by {groupQuiz.hostUsername}</p>
            </div>

            <div className="content">
                <div className="qrSection">
                    <h2>Scan to Join</h2>
                    <div className="qrCode">
                        <QRCodeSVG
                            value={joinUrl}
                            size={256}
                            level="H"
                            includeMargin={true}
                        />
                    </div>
                    <p className="joinCode">Join Code: <strong>{hash}</strong></p>
                    <div className="joinUrlContainer">
                        <p className="joinUrl">{joinUrl}</p>
                        <button
                            className="copyButton"
                            onClick={handleCopyUrl}
                            title="Copy to clipboard"
                            aria-label="Copy invite URL to clipboard"
                        >
                            {copied ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                </svg>
                            )}
                        </button>
                    </div>
                </div>

                <div className="participantsSection">
                    <h2>Participants ({participants.length})</h2>
                    {participants.length === 0 ? (
                        <p className="noParticipants">Waiting for participants to join...</p>
                    ) : (
                        <ul className="participantsList">
                            {participants.map((username, index) => (
                                <li key={index} className="participant">
                                    <span className="participantIcon">👤</span>
                                    {username}
                                    {connectionStatuses[username] === false && (
                                        <span className="disconnectedBadge" title="Disconnected">⚠️</span>
                                    )}
                                    {connectionStatuses[username] === true && (
                                        <span className="connectedBadge" title="Connected">✓</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <div className="actions">
                <button
                    className="startButton"
                    disabled={participants.length < 1}
                    onClick={handleStart}
                    title={participants.length < 1 ? 'Need at least 1 participant to start' : 'Start the quiz'}
                >
                    Start Quiz
                </button>
                <button
                    className="cancelButton"
                    onClick={handleCancel}
                >
                    Cancel
                </button>
            </div>
        </div>
    );
}
